/**
 * ExpertDashboard — Section "Dossiers Vérificateur reçus" (Phase 1 Mission
 * Prioritaire). Liste les handoffs (transmissions) reçus par le spécialiste depuis
 * le Vérificateur CONFILEX. Actions : accepter / décliner / consulter rapport.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import {
  Inbox, CheckCircle2, XCircle, Eye, Loader2, ShieldCheck, Clock, Fingerprint,
} from "lucide-react";

const STATUS_LABEL = {
  pending: "En attente",
  accepted: "Acceptée",
  declined: "Déclinée",
  revoked: "Révoquée",
};
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  revoked: "bg-slate-100 text-slate-600",
};

export default function ExpertHandoffsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [viewing, setViewing] = useState(null); // {handoff_id, data}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/expert/handoffs");
      setItems(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible de charger les dossiers reçus.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const accept = async (id) => {
    setBusy(id);
    try {
      await apiClient.post(`/expert/handoffs/${id}/accept`);
      toast.success("Dossier accepté");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally { setBusy(null); }
  };

  const decline = async (id) => {
    const reason = window.prompt("Motif (facultatif, max 200 caractères) :") || "";
    setBusy(id);
    try {
      await apiClient.post(`/expert/handoffs/${id}/decline`, { reason: reason.slice(0, 200) });
      toast.success("Dossier décliné");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally { setBusy(null); }
  };

  const view = async (id) => {
    setBusy(id);
    try {
      const r = await apiClient.get(`/expert/handoffs/${id}/report`);
      setViewing({ handoff_id: id, data: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accès au rapport impossible");
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <Card className="p-6 rounded-2xl border-slate-100" data-testid="expert-handoffs-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#C9A961]" />
      </Card>
    );
  }

  return (
    <div data-testid="expert-handoffs-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1.5 flex items-center gap-1.5">
            <Inbox className="w-3.5 h-3.5" /> Vérificateur CONFILEX
          </div>
          <h2 className="font-serif text-2xl text-[#0B3A2A]">Dossiers reçus ({items.length})</h2>
          <p className="text-sm text-slate-500 mt-1">
            Transmissions directes depuis le Vérificateur — palier approfondi (89 €).
            <b> Aucune commission</b> ne vous est facturée.
          </p>
        </div>
      </div>

      {error && (
        <Card className="p-4 rounded-2xl border-rose-200 bg-rose-50 text-sm text-rose-700 mb-4">{error}</Card>
      )}

      {items.length === 0 ? (
        <Card className="p-8 rounded-2xl border-slate-100 text-center">
          <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">Aucun dossier reçu pour le moment.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((h) => (
            <Card key={h.id} className="p-5 rounded-2xl border-slate-100" data-testid={`expert-handoff-${h.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <h3 className="font-serif text-lg text-[#0B3A2A]">{h.doc_label || h.doc_type}</h3>
                    <Badge className={`rounded-full text-xs ${STATUS_COLOR[h.status]}`}>{STATUS_LABEL[h.status]}</Badge>
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px]">
                      <ShieldCheck className="w-3 h-3 mr-1" />Sans commission
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">
                    {h.doc_filename || "—"}
                    {" · "}<Clock className="w-3 h-3 inline -mt-0.5" /> {new Date(h.created_at).toLocaleString("fr-FR")}
                    {h.access_count > 0 && (
                      <span className="ml-2 text-slate-500">· Consulté {h.access_count} fois</span>
                    )}
                  </p>
                  {h.message && (
                    <p className="text-[12px] text-slate-700 italic mt-2 px-3 py-2 bg-amber-50/50 border-l-2 border-[#C9A961]">
                      « {h.message} »
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {h.status === "pending" && (
                    <>
                      <Button
                        size="sm"
                        className="rounded-full bg-[#7A9B7E] hover:bg-[#93B19A]"
                        onClick={() => accept(h.id)}
                        disabled={busy === h.id}
                        data-testid={`accept-handoff-${h.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />Accepter
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => decline(h.id)}
                        disabled={busy === h.id}
                        data-testid={`decline-handoff-${h.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />Décliner
                      </Button>
                    </>
                  )}
                  {(h.status === "accepted" || h.status === "pending") && (
                    <Button
                      size="sm"
                      className="rounded-full bg-[#0B3A2A] hover:bg-[#0B3A2A]"
                      onClick={() => view(h.id)}
                      disabled={busy === h.id}
                      data-testid={`view-handoff-${h.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />Consulter le rapport
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {viewing && (
        <ReportViewer
          data={viewing.data}
          onClose={() => { setViewing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ReportViewer({ data, onClose }) {
  const a = data?.analysis || {};
  const v = data?.vigilance || {};
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="expert-handoff-report-modal"
    >
      <Card className="w-full max-w-3xl my-6 p-6 bg-white">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1">Rapport reçu</div>
            <h3 className="font-serif text-2xl text-[#0B3A2A]">{data?.doc_type_label || data?.doc_type}</h3>
            <p className="text-xs text-slate-500">{data?.filename}</p>
          </div>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="close-handoff-report">Fermer</Button>
        </div>
        {data?.fingerprint && (
          <div className="mb-4 p-2 rounded bg-slate-50 border border-slate-200 text-[11px] font-mono text-slate-600 break-all">
            <Fingerprint className="w-3 h-3 inline mr-1" /> Empreinte : {data.fingerprint}
          </div>
        )}
        {data?.client_message && (
          <Card className="p-3 mb-4 border-amber-200 bg-amber-50/40 text-[13px] italic text-slate-700">
            « {data.client_message} »
          </Card>
        )}
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Indicateur de complétude</div>
          <div className="font-serif text-2xl text-[#0B3A2A]">{v.score ?? "—"}<span className="text-base text-slate-500">/100</span> · {v.label}</div>
        </div>
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Résumé factuel</div>
          <p className="text-sm text-slate-700 leading-relaxed">{a.summary || "—"}</p>
        </div>
        {a.key_facts?.length > 0 && (
          <Section title="Faits clés extraits" items={a.key_facts} />
        )}
        {a.missing_info?.length > 0 && (
          <Section title="Informations manquantes" items={a.missing_info} />
        )}
        {a.vigilance_points?.length > 0 && (
          <Section title="Points à clarifier" items={a.vigilance_points} />
        )}
        {a.questions_for_expert?.length > 0 && (
          <Section title="Questions à préparer" items={a.questions_for_expert} />
        )}
        <p className="text-[11px] text-slate-500 italic mt-4">
          Cette consultation est enregistrée dans l'historique opposable du dossier.
        </p>
      </Card>
    </div>
  );
}

function Section({ title, items }) {
  return (
    <div className="mb-4">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{title}</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-sm text-slate-700">
            <CheckCircle2 className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
